import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  buildInitialBabyObsState,
  toBabyObservationPayload,
  validateBabyObservations,
} from "features/pregnancy/utils/buildInitialBabyObsState";
import { showMutationError } from "utils/showMutationError";

import { useBabyObservations } from "./useBabyObservations";
import { useSaveBabyObservation } from "./useSaveBabyObservation";
import { useSavePostnatalConsultation } from "./useSavePostnatalConsultation";
import { useSendPostnatalMncmsMessage } from "./useSendPostnatalMncmsMessage";

// Orchestrates saving a postnatal consultation (KIN-1075): the mother-level
// fields live on the consultation, and one baby observation is saved per foetus.
// Owns the per-baby form state, seeding (from the observations endpoint on edit)
// and validation, so the form component is a thin consumer.
export const usePostnatalConsultationSave = ({
  tenantVal,
  patientId,
  pregnancyId,
  postNatalConsultation,
  pregnancy,
  form,
  closeView,
}) => {
  const foetuses = pregnancy?.foetuses?.filter((foetus) => foetus.isActive) ?? [];

  const [savedConsultationId, setSavedConsultationId] = useState(
    postNatalConsultation?.id ?? null
  );
  const [wasSent, setWasSent] = useState(
    !!(postNatalConsultation?.sentToMncms || postNatalConsultation?.mncmsMessageId)
  );
  const [sendError, setSendError] = useState(null);
  const finishMode = useRef(null);

  const consultationId = savedConsultationId ?? postNatalConsultation?.id ?? null;

  // Seed per-baby state from any existing observations when editing.
  const { data: existingObservations } = useBabyObservations(
    postNatalConsultation?.id ?? null
  );
  const [babyObservations, setBabyObservations] = useState(() =>
    buildInitialBabyObsState(foetuses, [])
  );
  const [babyErrors, setBabyErrors] = useState({});

  useEffect(() => {
    if (existingObservations?.length) {
      setBabyObservations(buildInitialBabyObsState(foetuses, existingObservations));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingObservations]);

  const updateBaby = (foetusIdentifier, updated) =>
    setBabyObservations((prev) => ({ ...prev, [foetusIdentifier]: updated }));

  const { mutate: sendPostnatalMessage, isPending: isSendPending } =
    useSendPostnatalMncmsMessage(pregnancyId);
  const { mutate: finishConsultation, isPending } =
    useSavePostnatalConsultation(pregnancyId);
  const { mutateAsync: saveBabyObservation } = useSaveBabyObservation();

  const saveBabies = (parentConsultationId) =>
    Promise.all(
      Object.values(babyObservations).map((baby) =>
        saveBabyObservation(toBabyObservationPayload(baby, parentConsultationId))
      )
    );

  const save = (motherValues, mode) => {
    const errors = validateBabyObservations(babyObservations);
    if (Object.keys(errors).length > 0) {
      setBabyErrors(errors);
      toast.error("Complete the required fields for each baby.");

      return;
    }
    setBabyErrors({});

    finishMode.current = mode;
    // The consultation keeps the mother-level fields plus the first baby's data
    // in its legacy columns (backward compat); every baby is also persisted as
    // its own observation below.
    const primaryIdentifier = foetuses[0]?.foetusIdentifier;
    const consultationValues = {
      ...motherValues,
      ...(babyObservations[primaryIdentifier] ?? {}),
    };

    finishConsultation(
      { values: consultationValues, tenantVal, patientId, consultationId },
      {
        onSuccess: async (data) => {
          const savedId = data?.id ?? consultationId;

          if (data?.id) setSavedConsultationId(data.id);

          try {
            await saveBabies(savedId);
          } catch (error) {
            showMutationError(error);

            return;
          }

          const currentMode = finishMode.current;
          finishMode.current = null;

          if (currentMode === "send") {
            setSendError(null);
            sendPostnatalMessage(
              { consultationId: savedId, isCorrection: wasSent },
              {
                onSuccess: () => {
                  setWasSent(true);
                  form.reset();
                  closeView();
                },
                onError: () => setSendError("Send failed. Please try again."),
              }
            );
          } else if (currentMode === "close") {
            form.reset();
            closeView();
          }
        },
        onError: (error) => showMutationError(error),
      }
    );
  };

  const handleFinish = form.handleSubmit((values) => save(values, "close"));
  const handleFinishAndSend = form.handleSubmit((values) => save(values, "send"));

  const handleRetrySend = () => {
    const idToSend = savedConsultationId ?? postNatalConsultation?.id;
    if (!idToSend) return;

    setSendError(null);
    sendPostnatalMessage(
      { consultationId: idToSend, isCorrection: wasSent },
      {
        onSuccess: () => {
          setWasSent(true);
          form.reset();
          closeView();
        },
        onError: () => setSendError("Send failed. Please try again."),
      }
    );
  };

  return {
    consultationId,
    foetuses,
    babyObservations,
    babyErrors,
    updateBaby,
    wasSent,
    sendError,
    isPending,
    isSendPending,
    handleFinish,
    handleFinishAndSend,
    handleRetrySend,
  };
};
